import { useCallback, useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Download, Mail, Calendar, Plus, Trash2,
  BarChart3, ThumbsUp, MessageSquare, BookOpen,
  Play, History, CheckCircle, XCircle, Loader2, AlertTriangle,
  Tags, FileWarning, Bell, Sparkles, ArrowRight, Trophy,
  Code2, FileText, Table2
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { PageBanner } from '@/components/PageBanner';
import { PageHeader } from '@/components/PageHeader';
import { KpiCard } from '@/components/KpiCard';
import { PageShell } from '@/components/PageShell';
import { useAuth } from '@/contexts/AuthContext';
import { Skeleton } from '@/components/ui/skeleton';
import { LimitMeter } from '@/components/LimitMeter';
import { EmptyState } from '@/components/EmptyState';
import { DataTableSkeleton } from '@/components/DataTableControls';

const REPORT_TYPES = [
  {
    id: 'usage',
    label: 'Usage Report',
    shortLabel: 'Usage',
    icon: BarChart3,
    color: 'text-info',
    tone: 'bg-blue-500/10 border-blue-500/25',
    menuGroup: 'People',
    source: 'Usage page',
    description: 'Adoption, engagement, usage trends, and regional participation.',
    includes: ['KPI strip', 'Usage trend', 'Regional detail'],
  },
  {
    id: 'feedback',
    label: 'Feedback Report',
    shortLabel: 'Feedback',
    icon: ThumbsUp,
    color: 'text-success',
    tone: 'bg-green-500/10 border-green-500/25',
    menuGroup: 'Conversations',
    source: 'Feedback page',
    description: 'Sentiment KPIs plus specific feedback with region, country, question, answer, and comments.',
    includes: ['Sentiment KPIs', 'Feedback trend', 'Specific feedback'],
  },
  {
    id: 'conversations',
    label: 'Conversation Insights',
    shortLabel: 'Conversations',
    icon: MessageSquare,
    color: 'text-primary',
    tone: 'bg-purple-500/10 border-purple-500/25',
    menuGroup: 'Conversations',
    source: 'Conversations page',
    description: 'Conversation volume, participation, department activity, and expandable full summaries.',
    includes: ['Conversation KPIs', 'Department activity', 'Full summaries'],
  },
  {
    id: 'knowledge-base',
    label: 'Knowledge Base Report',
    shortLabel: 'Knowledge Base',
    icon: BookOpen,
    color: 'text-warning',
    tone: 'bg-amber-500/10 border-amber-500/25',
    menuGroup: 'Knowledge Base',
    source: 'Knowledge Base page',
    description: 'Document inventory, metadata, topic distribution, and publication status.',
    includes: ['Document KPIs', 'Topic distribution', 'Metadata table'],
  },
  {
    id: 'topics',
    label: 'Topics Report',
    shortLabel: 'Topics',
    icon: Tags,
    color: 'text-teal-500',
    tone: 'bg-teal-500/10 border-teal-500/25',
    menuGroup: 'Conversations',
    source: 'Topics and Conversations pages',
    description: 'Topic taxonomy, classification volume, document coverage, and most discussed topics.',
    includes: ['Topic KPIs', 'Most discussed', 'Topic detail'],
  },
  {
    id: 'content-gaps',
    label: 'Content Gaps Report',
    shortLabel: 'Content Gaps',
    icon: FileWarning,
    color: 'text-destructive',
    tone: 'bg-red-500/10 border-red-500/25',
    menuGroup: 'Knowledge Base',
    source: 'Content Gaps page',
    description: 'Open and resolved content gaps with questions, topics, geography, and responses.',
    includes: ['Gap KPIs', 'Gaps by type', 'Gap detail'],
  },
  {
    id: 'notifications',
    label: 'Notifications Report',
    shortLabel: 'Notifications',
    icon: Bell,
    color: 'text-sky-500',
    tone: 'bg-sky-500/10 border-sky-500/25',
    menuGroup: 'Engagement',
    source: 'Notifications page',
    description: 'Notification delivery and engagement across People, Email, and Click channels.',
    includes: ['Delivery KPIs', 'Channel stats', 'Notification detail'],
  },
  {
    id: 'leaderboard',
    label: 'Leaderboard Report',
    shortLabel: 'Leaderboard',
    icon: Trophy,
    color: 'text-yellow-500',
    tone: 'bg-yellow-500/10 border-yellow-500/25',
    menuGroup: 'Engagement',
    source: 'Leaderboard page',
    description: 'Employee engagement ranks, points, levels, streaks, badges, and top performers.',
    includes: ['Leaderboard KPIs', 'Top performers', 'Engagement detail'],
  },
];

const REPORT_GROUPS = ['Knowledge Base', 'People', 'Conversations', 'Engagement'];

const SCHEDULES = [
  { id: 'daily', label: 'Daily', description: 'Every day at 8:00 AM' },
  { id: 'weekly', label: 'Weekly', description: 'Every Monday at 8:00 AM' },
  { id: 'monthly', label: 'Monthly', description: 'First day of month at 8:00 AM' },
];

const EXPORT_FORMATS = [
  { id: 'html', label: 'HTML', description: 'Interactive report', icon: Code2 },
  { id: 'pdf', label: 'PDF', description: 'Printable layout', icon: FileText },
  { id: 'csv', label: 'CSV', description: 'Data tables', icon: Table2 },
];

const EXPORT_WINDOWS = [
  { id: '7d', label: 'Last 7 days' },
  { id: '14d', label: 'Last 14 days' },
  { id: '30d', label: 'Last 30 days' },
  { id: 'custom', label: 'Custom' },
];

const MAX_CUSTOM_RANGE_DAYS = 366;
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

function formatLimitValue(limit) {
  if (limit === -1) return 'Unlimited';
  if (limit === undefined || limit === null) return 'Not configured';
  return Number(limit).toLocaleString();
}

function formatRemainingLimit(limit, used) {
  if (limit === -1) return 'Unlimited remaining';
  if (limit === undefined || limit === null) return 'Limit not configured';
  return `${Math.max(0, Number(limit) - Number(used || 0)).toLocaleString()} remaining`;
}

function getUsagePercent(limit, used) {
  if (!limit || limit === -1) return null;
  return Math.min(100, Math.round((Number(used || 0) / Number(limit)) * 100));
}

function ReportLimitMeter({ kind, limits, loading }) {
  if (loading) {
    return <Skeleton className="h-40 w-full rounded-lg" />;
  }

  const planLimits = limits?.limits ?? {};
  const usage = limits?.usage ?? {};
  const isScheduled = kind === 'scheduled';

  return (
    <LimitMeter
      title={isScheduled ? 'Scheduled Reports' : 'Monthly Exports'}
      used={isScheduled ? usage.scheduled_reports || 0 : usage.monthly_exports || 0}
      limit={isScheduled ? planLimits.scheduled_reports_limit : planLimits.monthly_export_limit}
      icon={isScheduled ? Mail : Download}
      displayType={isScheduled ? 'capacity' : 'metered'}
      caption={isScheduled ? 'Active automated report schedules' : 'Exports generated in the last 30 days'}
      showManagePlanCta
      testId={isScheduled ? 'reports-scheduled-limit' : 'reports-export-limit'}
    />
  );
}

function buildExportPayload(exportConfig) {
  const payload = {
    report_type: exportConfig.report_type,
    format: exportConfig.format,
    window: exportConfig.window,
  };

  if (exportConfig.window === 'custom' && exportConfig.start_date && exportConfig.end_date) {
    payload.start_date = exportConfig.start_date;
    payload.end_date = exportConfig.end_date;
    payload.window = '30d';
  }

  return payload;
}

function getCustomRangeDays(startDate, endDate) {
  const start = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);
  return Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
}

function getTodayDateString() {
  return new Date().toISOString().slice(0, 10);
}

function ReportTypeSelector({ value, onChange }) {
  return (
    <div className="max-h-[560px] overflow-y-auto rounded-xl border border-border bg-secondary/20 p-3">
      <div className="sticky top-0 z-10 mb-3 bg-card/95 px-1 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur">
        Report Type
      </div>
      <div className="space-y-2">
        {REPORT_GROUPS.map((group) => {
          const reports = REPORT_TYPES.filter((report) => report.menuGroup === group);
          if (!reports.length) return null;
          return (
            <div key={group} className="space-y-1.5">
              <div className="px-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground first:pt-0">
                {group}
              </div>
              {reports.map((report) => {
                const Icon = report.icon;
                const isSelected = report.id === value;
                return (
                  <button
                    key={report.id}
                    type="button"
                    data-testid={`report-type-${report.id}`}
                    onClick={() => onChange(report.id)}
                    className={`w-full rounded-lg border p-3 text-left transition hover:bg-secondary ${
                      isSelected ? `${report.tone} shadow-sm ring-1 ring-primary/30` : 'border-transparent bg-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-card ${report.color}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-semibold text-foreground">{report.shortLabel}</div>
                        <div className="truncate text-xs text-muted-foreground">{report.source}</div>
                      </div>
                      {isSelected && <ArrowRight className="h-4 w-4 shrink-0 text-primary" />}
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReportSummary({ report }) {
  const SelectedIcon = report.icon;
  return (
    <>
      <div className="flex gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-card shadow-sm">
          <SelectedIcon className={`h-6 w-6 ${report.color}`} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">{report.label}</h3>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">{report.description}</p>
          <p className="mt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Source: {report.source}
          </p>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {report.includes.map((item) => (
          <Badge key={item} variant="secondary" className="rounded-full">
            {item}
          </Badge>
        ))}
      </div>
    </>
  );
}

async function getExportErrorMessage(err) {
  const responseData = err?.response?.data;

  if (responseData instanceof Blob) {
    try {
      const text = await responseData.text();
      const parsed = JSON.parse(text);
      return parsed?.detail || text || 'Failed to export report. Please try again.';
    } catch {
      return 'Failed to export report. Please try again.';
    }
  }

  return responseData?.detail || 'Failed to export report. Please try again.';
}

async function getExportResponseErrorDetail(response) {
  try {
    const text = await response.text();
    if (!text) return 'Failed to export report. Please try again.';

    try {
      const parsed = JSON.parse(text);
      return parsed?.detail || parsed?.message || text;
    } catch {
      return text;
    }
  } catch {
    return 'Failed to export report. Please try again.';
  }
}

async function postExportReport(payload) {
  let response;
  try {
    response = await fetch(`${API_BASE_URL}/export/report`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(localStorage.getItem('eva_token') ? { Authorization: `Bearer ${localStorage.getItem('eva_token')}` } : {}),
      },
      body: JSON.stringify(payload),
    });
  } catch (_error) {
    const networkError = new Error('Unable to connect to server. Please check if the backend is running.');
    networkError.response = {
      status: 0,
      data: { detail: networkError.message },
    };
    throw networkError;
  }

  if (!response.ok) {
    const detail = await getExportResponseErrorDetail(response);
    const error = new Error(detail);
    error.response = {
      status: response.status,
      data: { detail },
    };
    throw error;
  }

  return response.blob();
}

async function parseExportError(err, { canManageBilling = false } = {}) {
  try {
    const message = await getExportErrorMessage(err);
    const isMonthlyExportLimit = isMonthlyExportLimitError(err, message);
    if (isMonthlyExportLimit) {
      return getMonthlyExportLimitToast({ canManageBilling, message });
    }
    return { title: 'Export Failed', message };
  } catch {
    return {
      title: 'Export Failed',
      message: 'Failed to export report. Please try again.',
    };
  }
}

function isMonthlyExportLimitError(err, message) {
  return err?.response?.status === 403
    && (
      String(message).toLowerCase().includes('monthly export limit')
      || message === 'Failed to export report. Please try again.'
    );
}

function getMonthlyExportLimitMessage(message, { canManageBilling = false, limit = null } = {}) {
  const waitMessage = 'You can also wait until older exports roll out of the 30-day window.';
  const limitLabel = limit === null || limit === undefined ? '' : ` (${limit})`;
  if (!canManageBilling) {
    return `Monthly export limit reached${limitLabel}. Ask an admin to upgrade or adjust the limit. ${waitMessage}`;
  }
  if (message !== 'Failed to export report. Please try again.' && String(message).toLowerCase().includes('wait')) {
    return message;
  }
  if (message !== 'Failed to export report. Please try again.' && String(message).toLowerCase().includes('monthly export limit')) {
    return `${message} ${waitMessage}`;
  }
  return `Monthly export limit reached${limitLabel}. Upgrade your plan or ask an admin to adjust the limit. ${waitMessage}`;
}

function getMonthlyExportLimitToast({ canManageBilling = false, limit = null, message = null } = {}) {
  return {
    title: 'Monthly Export Limit Reached',
    message: getMonthlyExportLimitMessage(message || 'Failed to export report. Please try again.', { canManageBilling, limit }),
    action: canManageBilling ? {
      label: 'Upgrade Plan',
      onClick: () => {
        window.location.href = '/settings/billing';
      },
    } : undefined,
  };
}

function showExportErrorToast({ title, message, action }) {
  try {
    toast.error(title, {
      description: message,
      ...(action ? { action } : {}),
    });
  } catch {
    // Keep failed toast rendering from bubbling into the React runtime overlay.
  }
}

function isMonthlyExportLimitReached(reportLimits) {
  const limit = reportLimits?.limits?.monthly_export_limit;
  const used = reportLimits?.usage?.monthly_exports || 0;
  return Number.isFinite(Number(limit)) && Number(limit) !== -1 && Number(used) >= Number(limit);
}

function shouldLogExportError(err) {
  const status = err?.response?.status;
  return !status || status >= 500;
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  link.target = '_blank';
  link.style.display = 'none';
  document.body.appendChild(link);

  try {
    link.click();
  } finally {
    try {
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (cleanupError) {
      console.warn('Export cleanup failed:', cleanupError);
    }
  }
}

function ExportSection({ onLimitsChanged, canManageBilling = false, reportLimits = null }) {
  const [exporting, setExporting] = useState(false);
  const [exportConfig, setExportConfig] = useState({
    report_type: 'usage',
    format: 'html',  // Default to HTML (matches Envu style)
    window: '30d',
    start_date: '',
    end_date: '',
  });
  const selectedReport = REPORT_TYPES.find((report) => report.id === exportConfig.report_type) || REPORT_TYPES[0];

  const handleExport = async () => {
    if (exportConfig.window === 'custom') {
      if (Boolean(exportConfig.start_date) !== Boolean(exportConfig.end_date)) {
        toast.error('Export Failed', { description: 'Choose both a start date and an end date for a custom range.' });
        return;
      }

      if (exportConfig.start_date && exportConfig.end_date && exportConfig.start_date > exportConfig.end_date) {
        toast.error('Export Failed', { description: 'Start date must be on or before the end date.' });
        return;
      }

      const today = getTodayDateString();
      if (exportConfig.start_date > today || exportConfig.end_date > today) {
        toast.error('Export Failed', { description: 'Custom date range cannot include future dates.' });
        return;
      }

      if (exportConfig.start_date && exportConfig.end_date && getCustomRangeDays(exportConfig.start_date, exportConfig.end_date) > MAX_CUSTOM_RANGE_DAYS) {
        toast.error('Export Failed', { description: 'Custom date range cannot exceed 1 year.' });
        return;
      }
    }

    if (isMonthlyExportLimitReached(reportLimits)) {
      showExportErrorToast(getMonthlyExportLimitToast({
        canManageBilling,
        limit: reportLimits?.limits?.monthly_export_limit,
      }));
      return;
    }

    const payload = buildExportPayload(exportConfig);
    setExporting(true);
    try {
      const responseBlob = await postExportReport(payload);

      // Determine MIME type based on format
      const mimeTypes = {
        html: 'text/html',
        pdf: 'application/pdf',
        csv: 'text/csv',
      };
      
      const blob = new Blob([responseBlob], { type: mimeTypes[exportConfig.format] || responseBlob.type || 'application/octet-stream' });
      const timestamp = new Date().toISOString().replace(/[:.]/g, '').slice(0, 15);
      downloadBlob(blob, `eva_${exportConfig.report_type}_${timestamp}.${exportConfig.format}`);

      toast.success('Export Complete', { description: `${exportConfig.report_type} report exported as ${exportConfig.format.toUpperCase()}` });
      onLimitsChanged?.();
    } catch (err) {
      if (shouldLogExportError(err)) {
        console.error('Export error:', err);
      }
      const { title, message, action } = await parseExportError(err, { canManageBilling });
      showExportErrorToast({ title, message, action });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card className="bg-card border-border shadow-sm overflow-hidden">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-info" />
          Export Reports
        </CardTitle>
        <CardDescription className="mt-1 max-w-2xl">
          Choose a report mapped to its source dashboard, then export a polished HTML/PDF narrative or CSV data tables.
        </CardDescription>
      </CardHeader>
      <CardContent className="px-6 pb-6 pt-2 lg:px-7">
        <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(260px,0.85fr)_minmax(0,1.35fr)]">
          <ReportTypeSelector
            value={exportConfig.report_type}
            onChange={(reportType) => setExportConfig({ ...exportConfig, report_type: reportType })}
          />

          <div className={`min-h-[560px] self-start rounded-xl border p-6 ${selectedReport.tone}`}>
            <ReportSummary report={selectedReport} />

            <div className="mt-6 grid grid-cols-1 gap-5 xl:grid-cols-2">
              <div className="flex gap-3">
                <div className="w-full">
                  <Label>Output Format</Label>
                  <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3 xl:grid-cols-1">
                    {EXPORT_FORMATS.map((format) => {
                      const isSelected = exportConfig.format === format.id;
                      const FormatIcon = format.icon;
                      return (
                        <button
                          key={format.id}
                          type="button"
                          data-testid={`export-format-${format.id}`}
                          aria-pressed={isSelected}
                          onClick={() => setExportConfig({ ...exportConfig, format: format.id })}
                          className={`rounded-lg border p-3 text-left transition ${
                            isSelected ? 'border-primary bg-primary/10 text-foreground shadow-sm' : 'border-border bg-card/70 text-muted-foreground hover:bg-card'
                          }`}
                        >
                          <span className="flex items-center gap-2 font-semibold">
                            <FormatIcon className="h-4 w-4" />
                            {format.label}
                          </span>
                          <span className="mt-1 block pl-6 text-xs">{format.description}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div>
                <Label>Time Window</Label>
                <div className="mt-2 grid grid-cols-2 rounded-lg border border-border bg-card/70 p-1 sm:grid-cols-4" data-testid="export-window-select">
                  {EXPORT_WINDOWS.map((windowOption) => {
                    const isSelected = exportConfig.window === windowOption.id;
                    return (
                      <button
                        key={windowOption.id}
                        type="button"
                        data-testid={`export-window-${windowOption.id}`}
                        aria-pressed={isSelected}
                        onClick={() =>
                          setExportConfig((current) => ({
                            ...current,
                            window: windowOption.id,
                            start_date: windowOption.id === 'custom' ? current.start_date : '',
                            end_date: windowOption.id === 'custom' ? (current.end_date || getTodayDateString()) : '',
                          }))
                        }
                        className={`rounded-md px-3 py-2 text-center text-sm font-semibold transition ${
                          isSelected ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                        }`}
                      >
                        {windowOption.label}
                      </button>
                    );
                  })}
                </div>

                {exportConfig.window === 'custom' && (
                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-1">
                    <div>
                      <Label htmlFor="export-start-date">Start Date</Label>
                      <Input
                        id="export-start-date"
                        data-testid="export-start-date"
                        type="date"
                        max={getTodayDateString()}
                        value={exportConfig.start_date}
                        onChange={(e) => setExportConfig({ ...exportConfig, start_date: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="export-end-date">End Date</Label>
                      <Input
                        id="export-end-date"
                        data-testid="export-end-date"
                        type="date"
                        min={exportConfig.start_date || undefined}
                        max={getTodayDateString()}
                        value={exportConfig.end_date}
                        onChange={(e) => setExportConfig({ ...exportConfig, end_date: e.target.value })}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-3 border-t border-border/60 pt-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                {exportConfig.window === 'custom'
                  ? 'Choose exact start and end dates for the export.'
                  : 'Choose a preset rolling window for the export.'}
              </p>

              <Button onClick={handleExport} disabled={exporting} className="sm:w-auto" data-testid="export-btn">
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Exporting...' : `Export as ${exportConfig.format.toUpperCase()}`}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ScheduledReportsSection({ onLimitsChanged }) {
  const [reports, setReports] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningId, setRunningId] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [newReport, setNewReport] = useState({
    report_type: 'usage',
    schedule: 'weekly',
    recipients: '',
  });

  const fetchReports = async () => {
    try {
      const response = await api.get('/scheduled-reports');
      setReports(response.data);
    } catch (err) {
      console.error('Failed to fetch scheduled reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await api.get('/scheduled-reports/history?limit=20');
      setHistory(response.data);
    } catch (err) {
      console.error('Failed to fetch execution history:', err);
    }
  };

  useEffect(() => {
    fetchReports();
    fetchHistory();
  }, []);

  const handleCreate = async () => {
    try {
      await api.post('/scheduled-reports', {
        ...newReport,
        recipients: newReport.recipients.split(',').map((e) => e.trim()).filter(Boolean),
      });
      toast.success('Schedule Created', { description: 'Report schedule has been created successfully.' });
      fetchReports();
      onLimitsChanged?.();
      setNewReport({ report_type: 'usage', schedule: 'weekly', recipients: '' });
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to create schedule.';
      toast.error('Error', { description: errorMessage });
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/scheduled-reports/${id}`);
      toast.success('Schedule Deleted', { description: 'Report schedule has been deleted.' });
      fetchReports();
      fetchHistory();
      onLimitsChanged?.();
    } catch {
      toast.error('Error', { description: 'Failed to delete schedule.' });
    }
  };

  const handleRunNow = async (id) => {
    setRunningId(id);
    try {
      await api.post(`/scheduled-reports/${id}/run`);
      toast.success('Report Executed', { description: 'Report has been generated and emailed successfully.' });
      fetchReports();
      fetchHistory();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to execute report.';
      toast.error('Error', { description: errorMessage });
    } finally {
      onLimitsChanged?.();
      setRunningId(null);
    }
  };

  const getReportType = (id) => REPORT_TYPES.find((rt) => rt.id === id) || REPORT_TYPES[0];
  const selectedReport = getReportType(newReport.report_type);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Card className="bg-card border-border shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Calendar className="w-5 h-5 text-info" />
          Scheduled Reports
        </CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setHistoryDialogOpen(true)} data-testid="view-history-btn">
            <History className="w-4 h-4 mr-1" />
            History
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(260px,0.85fr)_minmax(0,1.35fr)]">
          <ReportTypeSelector
            value={newReport.report_type}
            onChange={(reportType) => setNewReport({ ...newReport, report_type: reportType })}
          />

          <div className={`min-h-[560px] self-start rounded-xl border p-6 ${selectedReport.tone}`}>
            <ReportSummary report={selectedReport} />

            <div className="mt-6">
              <Label>Frequency</Label>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3" data-testid="schedule-frequency-select">
                {SCHEDULES.map((schedule) => {
                  const isSelected = newReport.schedule === schedule.id;
                  return (
                    <button
                      key={schedule.id}
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => setNewReport({ ...newReport, schedule: schedule.id })}
                      className={`rounded-lg border p-3 text-left transition ${
                        isSelected ? 'border-primary bg-primary/10 text-foreground shadow-sm' : 'border-border bg-card/70 text-muted-foreground hover:bg-card'
                      }`}
                    >
                      <span className="block font-semibold">{schedule.label}</span>
                      <span className="mt-1 block text-xs">{schedule.description}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-5">
              <Label htmlFor="scheduled-recipients">Recipients</Label>
              <Input
                id="scheduled-recipients"
                placeholder="email1@company.com, email2@company.com"
                value={newReport.recipients}
                onChange={(e) => setNewReport({ ...newReport, recipients: e.target.value })}
                data-testid="recipients-input"
              />
            </div>

            <div className="mt-5 flex flex-col gap-3 border-t border-border/60 pt-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Scheduled reports are delivered by email as printable PDFs.
              </p>
              <Button onClick={handleCreate} data-testid="create-schedule-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Schedule
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <DataTableSkeleton
            rows={3}
            columns={4}
            showToolbar={false}
            data-testid="scheduled-reports-loading"
          />
        ) : reports.length === 0 ? (
          <EmptyState
            icon={Mail}
            title="No scheduled reports yet."
            description="Create a schedule to automatically send reports."
            variant="plain"
          />
        ) : (
          <div className="space-y-3">
            {reports.map((report) => {
              const rt = getReportType(report.report_type);
              // Parse recipients - could be JSON string or already an array
              let recipients = [];
              try {
                recipients = typeof report.recipients === 'string' 
                  ? JSON.parse(report.recipients || '[]') 
                  : (report.recipients || []);
              } catch {
                recipients = [];
              }
              const isRunning = runningId === report.id;
              return (
                <div
                  key={report.id}
                  className="flex items-center justify-between p-4 bg-secondary rounded-xl"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 bg-card rounded-xl flex items-center justify-center shadow-sm`}>
                      <rt.icon className={`w-5 h-5 ${rt.color}`} />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{rt.label}</p>
                      <p className="text-sm text-muted-foreground">
                        {report.schedule} · {recipients.length} recipient(s)
                        {report.last_sent && (
                          <span className="ml-2 text-xs">
                            · Last run: {formatDate(report.last_sent)}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={report.enabled ? 'default' : 'secondary'}>
                      {report.enabled ? 'Active' : 'Paused'}
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRunNow(report.id)}
                      disabled={isRunning}
                      data-testid={`run-now-${report.id}`}
                    >
                      {isRunning ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(report.id)}
                      data-testid={`delete-schedule-${report.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Execution History Dialog */}
        <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Execution History
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto">
              {history.length === 0 ? (
                <EmptyState
                  icon={History}
                  title="No execution history yet"
                  description="Report runs will appear here after a scheduled or manual export."
                  variant="plain"
                  compact
                />
              ) : (
                <div className="space-y-2">
                  {history.map((run) => {
                    const rt = getReportType(run.report_type);
                    const isSuccess = run.status === 'completed' || run.status === 'generated';
                    return (
                      <div
                        key={run.id}
                        className="flex items-center justify-between p-3 bg-secondary rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          {isSuccess ? (
                            <CheckCircle className="w-5 h-5 text-success" />
                          ) : (
                            <XCircle className="w-5 h-5 text-destructive" />
                          )}
                          <div>
                            <p className="font-medium text-sm">{rt.label}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(run.started_at)}
                              {run.recipients_count > 0 && ` · ${run.recipients_count} recipients`}
                              {run.file_size && ` · ${Math.round(run.file_size / 1024)}KB`}
                            </p>
                          </div>
                        </div>
                        <Badge variant={isSuccess ? 'default' : 'destructive'}>
                          {run.status}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default function ReportsPage() {
  const { hasRole } = useAuth();
  const canViewReports = hasRole(['admin', 'approver', 'analyst']);
  // Scheduling shares the same audience as report view/export (EIP-253): admins,
  // approvers, and analysts can all manage scheduled reports. `isAdmin` below is
  // kept only for the billing-upgrade CTA, which stays admin-only.
  const canScheduleReports = canViewReports;
  const isAdmin = hasRole(['admin']);
  const [activeReportsTab, setActiveReportsTab] = useState('exports');
  const [reportLimits, setReportLimits] = useState(null);
  const [reportLimitsLoading, setReportLimitsLoading] = useState(true);
  const planLimits = reportLimits?.limits ?? {};
  const reportUsage = reportLimits?.usage ?? {};
  const monthlyExportsUsed = reportUsage.monthly_exports || 0;
  const scheduledReportsUsed = reportUsage.scheduled_reports || 0;
  const monthlyExportLimit = planLimits.monthly_export_limit;
  const scheduledReportsLimit = planLimits.scheduled_reports_limit;
  const monthlyExportPercent = getUsagePercent(monthlyExportLimit, monthlyExportsUsed);
  const scheduledReportsPercent = getUsagePercent(scheduledReportsLimit, scheduledReportsUsed);

  const fetchReportLimits = useCallback((showLoading = false) => {
    if (showLoading) setReportLimitsLoading(true);
    return api.get('/settings/tenant/limits')
      .then((response) => {
        setReportLimits(response.data);
      })
      .catch((err) => {
        console.error('Failed to load report limits:', err);
      })
      .finally(() => {
        if (showLoading) setReportLimitsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!canViewReports) {
      setReportLimitsLoading(false);
      return undefined;
    }

    let mounted = true;
    setReportLimitsLoading(true);
    api.get('/settings/tenant/limits')
      .then((response) => {
        if (mounted) setReportLimits(response.data);
      })
      .catch((err) => {
        console.error('Failed to load report limits:', err);
      })
      .finally(() => {
        if (mounted) setReportLimitsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [canViewReports]);

  if (!canViewReports) {
    return (
      <PageShell animate={false}>
        <Card className="bg-warning/5 border-warning/30">
          <CardContent className="p-6 flex items-center gap-4">
            <AlertTriangle className="h-8 w-8 text-warning" />
            <div>
              <h3 className="font-semibold">Access Denied</h3>
              <p className="text-muted-foreground">Reports are only available to analysts, approvers, and administrators.</p>
            </div>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  return (
    <PageShell data-testid="reports-page">
      {/* Page Banner */}
      <PageBanner pageKey="engagement-reports" />
      
      <PageHeader title="Reports" description="Create dashboard-mapped HTML, PDF, and CSV reports with scheduled delivery" />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          title="Exports (30d)"
          value={reportLimitsLoading ? '...' : monthlyExportsUsed.toLocaleString()}
          subtitle={reportLimitsLoading ? 'Loading usage' : `${monthlyExportPercent ?? 0}% of monthly limit used`}
          icon={BarChart3}
          color="info"
        />
        <KpiCard
          title="Export Capacity"
          value={reportLimitsLoading ? '...' : formatLimitValue(monthlyExportLimit)}
          subtitle={reportLimitsLoading ? 'Loading limit' : formatRemainingLimit(monthlyExportLimit, monthlyExportsUsed)}
          icon={Download}
          color="success"
        />
        <KpiCard
          title="Active Schedules"
          value={reportLimitsLoading ? '...' : scheduledReportsUsed.toLocaleString()}
          subtitle={reportLimitsLoading ? 'Loading usage' : `${scheduledReportsPercent ?? 0}% of schedule limit used`}
          icon={Calendar}
          color="primary"
        />
        <KpiCard
          title="Schedule Capacity"
          value={reportLimitsLoading ? '...' : formatLimitValue(scheduledReportsLimit)}
          subtitle={reportLimitsLoading ? 'Loading limit' : formatRemainingLimit(scheduledReportsLimit, scheduledReportsUsed)}
          icon={Mail}
          color="warning"
        />
      </div>

      <Tabs value={activeReportsTab} onValueChange={setActiveReportsTab} className="space-y-6">
        <TabsList className={`grid w-full ${canScheduleReports ? 'grid-cols-2' : 'grid-cols-1'} sm:w-auto`}>
          <TabsTrigger value="exports" data-testid="reports-export-tab" className="gap-2" onClick={() => setActiveReportsTab('exports')}>
            <Download className="h-4 w-4" />
            Exports
          </TabsTrigger>
          {canScheduleReports ? (
            <TabsTrigger value="scheduled" data-testid="reports-scheduled-tab" className="gap-2" onClick={() => setActiveReportsTab('scheduled')}>
              <Calendar className="h-4 w-4" />
              Scheduled Reports
            </TabsTrigger>
          ) : null}
        </TabsList>

        <TabsContent value="exports" className="space-y-6">
          <ReportLimitMeter kind="exports" limits={reportLimits} loading={reportLimitsLoading} />
          <ExportSection onLimitsChanged={fetchReportLimits} canManageBilling={isAdmin} reportLimits={reportLimits} />
        </TabsContent>

        {canScheduleReports ? (
          <TabsContent value="scheduled" className="space-y-6">
            <ReportLimitMeter kind="scheduled" limits={reportLimits} loading={reportLimitsLoading} />
            <ScheduledReportsSection onLimitsChanged={fetchReportLimits} />
          </TabsContent>
        ) : null}
      </Tabs>

      {/* Email Configuration Note - only show if email not configured */}
      {/* This note is intentionally hidden - email configuration is managed in Integrations page */}
    </PageShell>
  );
}
